import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import { KonvaRenderer } from 'react-konva';
import roofDataReducer from 'store/slices/RoofDataSlice/roofDataSlice';
import energyProviderReducer from 'store/slices/EnergySlice/energySlice';
import offsetModelReducer from 'store/slices/OffsetModelSlice/offsetmodelSlice';
import authProviderReducer from 'store/slices/AuthSlice/authSlice';
import QueryParamReducer, { setDisplayMode } from 'store/slices/QueryParamsSlice';
import { getDisplayModeFromUrl } from 'store/slices/QueryParamsSlice/util/getInitialState';
import IrradianceReducer from 'store/slices/IrradianceSlice';
import ToolNewPostionReducer from 'store/slices/ToolSlice';
import DeleteModalReducer from 'store/slices/DeleteModalSlice';
import sessionStorage from 'redux-persist/es/storage/session';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import EditFacetModalReducer from 'store/slices/EditFacetModalSlice';
import ChangeOrientationModalSlice from './slices/ChangeOrientationModalSlice';
import WarningModalSlice from './slices/WarningSlice';
import ManualToolModalSlice from './slices/ManualToolModal';
import addPanelModalSlice from './slices/AddPanelModalSlice/addPanelModalSlice';
import InfoModalSlice from './slices/InfoSlice';
import PanelSlice from './slices/PanelSlice';
import designSettingReducer from './slices/DesignSettingsSlice';
import recalculateLayoutModalReducer from './slices/RecalculateLayoutSlice';
import resetChangesModalReducer from './slices/ResetChangesSlice';
import debugDialogSliceReducer from './slices/DebugDialogSlice';
import RoofToolLinesSlice from './slices/RoofToolLinesSlice';
import roofEditReducer from 'store/slices/RoofEditSlice';
import RoleReducer from 'store/slices/RoleSlice';
import SaveDesignReducer from 'store/slices/SaveDesignSlice/SaveDesignSlice';
import UndoObstructionReducer from 'store/slices/ObstructionUndoSlice';
import AdjustShadingReducer from 'store/slices/AdjustShadingSlice';
import CustomMapReducer from './slices/CustomMapSlice';
import TreeReducer from './slices/TreeSlice';
import DsmShadingReducer from './slices/DsmShadingSlice';
import DesignCreateReducer from './slices/DesignCreateSlice';
import { dsmShadingListener } from './middleware/dsmShadingListener';
import { designCreateListener } from './middleware/designCreateListener';
import { irradianceListener } from './middleware/irradianceListener';

const persistConfig = {
	key: 'root',
	storage: sessionStorage,
	stateReconciler: autoMergeLevel2,
	blacklist: [
		'toolNewPostions',
		'deleteModal',
		'editFacetModal',
		'Irradiance',
		'changeOrientationModal',
		'warningModal',
		'manualToolModal',
		'panelSlice',
		'designSettigs',
		'roofToolLines',
		'roofEditData',
		'saveDesignState',
		'customMap',
		'treeSlice',
		'dsmShading',
		'designCreate'
	],
};

const rootReducer = combineReducers({
	roofData: roofDataReducer,
	energyData: energyProviderReducer,
	offsetModal: offsetModelReducer,
	authProvider: authProviderReducer,
	QueryParams: QueryParamReducer,
	Irradiance: IrradianceReducer,
	toolNewPostions: ToolNewPostionReducer,
	deleteModal: DeleteModalReducer,
	editFacetModal: EditFacetModalReducer,
	changeOrientationModal: ChangeOrientationModalSlice,
	warningModal: WarningModalSlice,
	manualToolModal: ManualToolModalSlice,
	addPanelModal: addPanelModalSlice,
	infoModal: InfoModalSlice,
	panelSlice: PanelSlice,
	designSettigs: designSettingReducer,
	recalculateLayoutModal: recalculateLayoutModalReducer,
	resetChangesModal: resetChangesModalReducer,
	debugDialog: debugDialogSliceReducer,
	roofToolLines: RoofToolLinesSlice,
	roofEditData: roofEditReducer,
	roleState: RoleReducer,
	saveDesignState: SaveDesignReducer,
	obstructionState: UndoObstructionReducer,
	adjustShadingState: AdjustShadingReducer,
	customMap: CustomMapReducer,
	treeSlice: TreeReducer,
	dsmShading: DsmShadingReducer,
	designCreate: DesignCreateReducer
});

const persistedReducers = persistReducer<ReturnType<typeof rootReducer>>(persistConfig, rootReducer);

// const loggerMiddleware = store => next => action => {
// 	console.log('Dispatching:', action);
// 	console.log('Before State:', store.getState());
// 	const result = next(action);
// 	console.log('After State:', store.getState());
// 	return result;
// };

// react-redux batches store notifications for react-dom only. react-konva renders the canvas with its own
// legacy-mode renderer, which commits each un-batched update immediately — so one action re-rendered the
// canvas once per subscribed component (e.g. every panel). Batching here makes it a single canvas commit.
const batchCanvasUpdates: Middleware = () => (next) => (action) =>
	KonvaRenderer.batchedUpdates(() => next(action), undefined);

export const store = configureStore({
	reducer: persistedReducers,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE],
			},
		}).prepend(
			batchCanvasUpdates, designCreateListener.middleware, irradianceListener.middleware, dsmShadingListener.middleware
		)
});

// queryParamsProvider is persisted, so a reload would bring back the last session's displayMode.
// Let the URL win once per load, after rehydration; DISPLAY_MODE_CHANGED can still change it later.
export const persistor = persistStore(store, null, () => {
	store.dispatch(setDisplayMode(getDisplayModeFromUrl()));
});
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch